"use client";

import { Text } from "@pamperedchef/design-system";
import * as styles from "./ProductRating.module.scss";

export interface ProductRatingProps {
	value: number;
	count: number;
	reviewsHref?: string;
	max?: number;
	className?: string;
}

// DS-OVERRIDE (TPC-175013): not from DS — DS Rating renders an outline glyph for
// every star and never fills, so use a custom solid-star two-layer bar. Replace
// with DS Rating once it supports partial fill. Remove after DS fix [G4].
const STAR_SIZE = 20;
const SOLID_STAR_D =
	"M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27l6.91-1.01L12 2z";

interface SolidStarProps {
	size: number;
}

function SolidStar({ size }: SolidStarProps): JSX.Element {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="currentColor"
			aria-hidden="true"
		>
			<path d={SOLID_STAR_D} />
		</svg>
	);
}

export function ProductRating({
	value,
	count,
	reviewsHref,
	max = 5,
	className
}: ProductRatingProps) {
	if (!count || count <= 0) return null;

	const fillPercent = Math.max(0, Math.min(1, value / max)) * 100;
	const label = `${value.toFixed(1)} out of ${max} stars, ${count} reviews`;

	const summary = (
		<>
			<span className={styles["bar"]} aria-hidden="true">
				<span className={styles["bar__empty"]}>
					{Array.from({ length: max }, (_, i) => (
						<SolidStar key={i} size={STAR_SIZE} />
					))}
				</span>
				<span
					className={styles["bar__filled"]}
					style={{ width: `${fillPercent}%` }}
				>
					{Array.from({ length: max }, (_, i) => (
						<SolidStar key={i} size={STAR_SIZE} />
					))}
				</span>
			</span>

			<Text tag="span" styledAs="smr" className={styles["rating__value"]}>
				{`${value.toFixed(1)} Stars`}
			</Text>

			<Text tag="span" styledAs="smr" className={styles["rating__text"]}>
				{`(${count} Reviews)`}
			</Text>
		</>
	);

	const rootClass = [styles["rating"], className ?? ""]
		.filter(Boolean)
		.join(" ");

	if (reviewsHref) {
		return (
			<a
				href={reviewsHref}
				className={[rootClass, styles["rating--link"]].join(" ")}
				aria-label={label}
				data-component="ProductRating"
			>
				{summary}
			</a>
		);
	}

	return (
		<div
			className={rootClass}
			role="img"
			aria-label={label}
			data-component="ProductRating"
		>
			{summary}
		</div>
	);
}
